/**
 * MemberLocationService
 * Service for MemberLocation entity operations
 * Route: api/member-locations
 * Controller: MemberLocationsController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * MemberLocationService
 * Handles member location CRUD operations (composite key: MemberID + LocationID)
 * Maps to /api/member-locations endpoint
 */
export class MemberLocationService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MemberLocationService.DEFAULT_SERVICE_PATH,
            basePath: MemberLocationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== OVERRIDE: GET BY COMPOSITE KEY ====================
    /**
     * Get member location by composite key (memberId + locationId)
     * GET /api/member-locations/{memberId}/{locationId}
     * @param memberId - Member ID
     * @param locationId - Location ID
     * @returns Member location
     */
    async getByCompositeId(memberId, locationId) {
        const response = await this.getByCompositeIdRaw(memberId, locationId);
        if (!response.data) {
            throw new Error(`Member location with MemberID ${memberId} and LocationID ${locationId} not found`);
        }
        return response.data;
    }
    /**
     * Get member location by composite key (raw)
     * @param memberId - Member ID
     * @param locationId - Location ID
     * @returns ResponseData with member location
     */
    getByCompositeIdRaw(memberId, locationId) {
        const endpoint = this.getEndpoint('getByCompositeId', `${memberId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Delete member location by composite key
     * DELETE /api/member-locations/{memberId}/{locationId}
     * @param memberId - Member ID
     * @param locationId - Location ID
     * @returns true if successful
     */
    async deleteByCompositeId(memberId, locationId) {
        const response = await this.deleteByCompositeIdRaw(memberId, locationId);
        return response.success;
    }
    /**
     * Delete member location by composite key (raw)
     * @param memberId - Member ID
     * @param locationId - Location ID
     * @returns ResponseData
     */
    deleteByCompositeIdRaw(memberId, locationId) {
        const endpoint = this.getEndpoint('deleteByCompositeId', `${memberId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    /**
     * Update member location by composite key
     * PUT /api/member-locations/{memberId}/{locationId}
     * @param memberId - Member ID
     * @param locationId - Location ID
     * @param data - Updated member location data
     * @returns Updated member location
     */
    async updateByCompositeId(memberId, locationId, data) {
        const response = await this.updateByCompositeIdRaw(memberId, locationId, data);
        if (!response.data) {
            throw new Error(`Failed to update member location ${memberId}/${locationId}`);
        }
        return response.data;
    }
    /**
     * Update member location by composite key (raw)
     * @param memberId - Member ID
     * @param locationId - Location ID
     * @param data - Updated member location data
     * @returns ResponseData with updated member location
     */
    updateByCompositeIdRaw(memberId, locationId, data) {
        const endpoint = this.getEndpoint('updateByCompositeId', `${memberId}/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, data);
    }
}
MemberLocationService.DEFAULT_SERVICE_PATH = 'members';
MemberLocationService.BASE_PATH = 'api/member-locations';
//# sourceMappingURL=MemberLocationService.js.map